import useDisableShiftTextSelectionRef from '../../hooks/useDisableShiftTextSelectionRef';
import useOnUpdate from '../../hooks/useOnUpdate';
import { onColumnSizeChangedEventArgs } from '../../typings/grid/onColumnSizeChangedEventArgs';
import GridPagination, { IGridPaginationState } from './gridPagination';
import GridRowSelectorCell from './gridRowSelectorCell';
import GridRowSelectorHeader from './gridRowSelectorHeader';
import {
	LibraryResultsGridPaginationStyle,
	LibraryResultsGridStyle,
	libraryResultsRootStyle,
	muiTableBodyCellPropsStyle,
	muiTableBodyRowPropsStyle,
	muiTableContainerPropsStyle,
	muiTableHeadCellPropsStyle,
	muiTableHeadRowPropsStyle,
	muiTablePaperPropsStyle
} from './gridStyles';
import {
	MaterialReactTable,
	MRT_ColumnDef,
	MRT_ColumnSizingState,
	MRT_Row,
	MRT_RowSelectionState,
	MRT_RowVirtualizer,
	MRT_SortingState,
	MRT_TableInstance,
	MRT_Updater,
	MRT_VisibilityState,
	useMaterialReactTable
} from 'material-react-table';
import React, { useCallback, useImperativeHandle, useState } from 'react';

const MinColumnSize = 32;

export interface IGridProps<T> {
	instanceRef?: React.RefObject<IGridInstance<T> | null>;
	columns: MRT_ColumnDef<T>[];
	data: T[];
	total: number;
	columnsVisibility?: MRT_VisibilityState;
	isLoading?: boolean;
	selectedItems: MRT_RowSelectionState;
	onRowSelectionChange: (state: MRT_RowSelectionState) => void;
	pagination: IGridPaginationState;
	onPaginationChange: (pagination: IGridPaginationState) => void;
	sorting: MRT_SortingState;
	onSortingChange: (sorting: MRT_SortingState) => void;
	testId?: string;
	onRowContextMenu?: (row: MRT_Row<T>) => void;
	onRowDoubleClick?: (row: MRT_Row<T>) => void;
	onRowClick?: (row: MRT_Row<T>, event?: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
	onRowCheckboxClick?: (row: MRT_Row<T>, event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
	onColumnSizeChanged?: (columnsSizeInfo: onColumnSizeChangedEventArgs) => void;
	estimateRowHeight?: () => number;
}

export interface IGridInstance<T> {
	table: MRT_TableInstance<T>;
	rowVirtualizer?: MRT_RowVirtualizer;
}

const CheckBoxRowSelectorColumn = 'mrt-row-select';

function getColumnSizeState<T>(column: MRT_ColumnDef<T>): number {
	if (!column) {
		return null;
	}

	if (column.enableResizing && !column.enableHiding) {
		return column.size ?? MinColumnSize;
	}

	return null;
}

const Grid = <T extends { id: number }>(props: IGridProps<T>): React.ReactElement => {
	const rootRef = useDisableShiftTextSelectionRef<HTMLDivElement>();
	const rowVirtualizerRef = React.useRef(null);

	const initialSizingState: Record<string, number> = {};
	// predefined accessorKey: mrt-row-select
	// in case when we need to manage enableRowSelection-property externally
	// should be updated to don't include this column into the state-array
	const initialColumnOrderState: string[] = [CheckBoxRowSelectorColumn];

	React.useMemo(() => {
		props.columns?.forEach(column => {
			initialSizingState[column.accessorKey] = getColumnSizeState(column);
			initialColumnOrderState.push(column.accessorKey);
		});
	}, []);

	const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>(initialSizingState ?? {});
	const [prevColumnSizing, setPrevColumnSizing] = useState<MRT_ColumnSizingState>(initialSizingState ?? {});

	useOnUpdate(() => {
		if (!props.onColumnSizeChanged) {
			return;
		}

		const columnKeys = [...new Set<string>(Object.keys(columnSizing).concat(Object.keys(prevColumnSizing)))];
		const stateDiff = {};
		columnKeys.forEach(key => {
			if (columnSizing[key] !== prevColumnSizing[key]) {
				stateDiff[key] = columnSizing[key];
			}
		});
		setPrevColumnSizing(columnSizing);
		props.onColumnSizeChanged({ currentState: columnSizing, stateDiff });
	}, [columnSizing]);

	const handleResizing = useCallback(
		(updaterOrValue: MRT_Updater<MRT_ColumnSizingState>) => {
			if (updaterOrValue instanceof Function) {
				setColumnSizing(updaterOrValue(columnSizing));
			} else {
				setColumnSizing(updaterOrValue);
			}
		},
		[columnSizing, setColumnSizing]
	);

	const rowsRenderedOutsideVisibleBoundaries = 5;
	const rowSelectorHeaderComponent = useCallback(
		(header: { table: MRT_TableInstance<T> }) => (
			<GridRowSelectorHeader testId={props.testId} table={header.table} />
		),
		[]
	);
	const rowSelectorCellComponent = useCallback(
		(cell: { row: MRT_Row<T> }) => (
			<GridRowSelectorCell testId={props.testId} row={cell.row} onRowCheckboxClick={props.onRowCheckboxClick} />
		),
		[]
	);

	const mrtTable = useMaterialReactTable({
		columns: props.columns,
		data: props.data,
		layoutMode: 'grid',
		enableRowVirtualization: true,
		rowVirtualizerOptions: {
			overscan: rowsRenderedOutsideVisibleBoundaries,
			estimateSize: props.estimateRowHeight
		},
		rowVirtualizerInstanceRef: rowVirtualizerRef,
		memoMode: 'cells',
		enableRowSelection: true,
		manualSorting: true,
		enableMultiSort: false,
		enableColumnResizing: true,
		enableSortingRemoval: false,
		enableGlobalFilter: false,
		enableFullScreenToggle: false,
		enableDensityToggle: false,
		enableHiding: false,
		enableColumnActions: false,
		enableFilters: false,
		enableTopToolbar: false,
		enableSelectAll: true,
		enableStickyHeader: true,
		enablePagination: false,
		enableBottomToolbar: false,
		onSortingChange: updaterOrValue => {
			updaterOrValue instanceof Function
				? props.onSortingChange(updaterOrValue(props.sorting))
				: props.onSortingChange(updaterOrValue);
		},
		getRowId: originalRow => originalRow.id?.toString(),
		columnResizeMode: 'onEnd',
		onColumnSizingChange: handleResizing,
		onRowSelectionChange: updaterOrValue => {
			updaterOrValue instanceof Function
				? props.onRowSelectionChange(updaterOrValue(props.selectedItems))
				: props.onRowSelectionChange(updaterOrValue);
		},
		initialState: { density: 'compact' },
		state: {
			sorting: props.sorting,
			isLoading: props.isLoading,
			columnVisibility: props.columnsVisibility,
			rowSelection: props.selectedItems,
			columnSizing: columnSizing,
			columnOrder: initialColumnOrderState
		},
		displayColumnDefOptions: {
			[CheckBoxRowSelectorColumn]: {
				header: '',
				size: 48,
				minSize: 48,
				Cell: rowSelectorCellComponent,
				Header: rowSelectorHeaderComponent,
				muiTableBodyCellProps: { align: 'center' },
				muiTableHeadCellProps: { align: 'center' },
				grow: false
			}
		},
		muiTableBodyRowProps: tableBody => {
			return {
				onDoubleClick: () => {
					if (props.onRowDoubleClick) {
						props.onRowDoubleClick(tableBody.row);
					}
				},
				onClick: event => {
					if (props.onRowClick) {
						props.onRowClick(tableBody.row, event);
					} else {
						tableBody.row.toggleSelected(!tableBody.row.getIsSelected());
					}
				},
				onContextMenu: () => {
					if (props.onRowContextMenu) {
						props.onRowContextMenu(tableBody.row);
					}
				},
				sx: muiTableBodyRowPropsStyle
			};
		},
		muiTablePaperProps: { sx: muiTablePaperPropsStyle },
		muiTableContainerProps: { sx: muiTableContainerPropsStyle },
		muiTableHeadRowProps: { sx: muiTableHeadRowPropsStyle },
		muiTableBodyCellProps: { sx: muiTableBodyCellPropsStyle },
		muiTableHeadCellProps: { sx: muiTableHeadCellPropsStyle }
	});

	useImperativeHandle(
		props.instanceRef,
		() => {
			return { table: mrtTable, rowVirtualizer: rowVirtualizerRef.current } as IGridInstance<T>;
		},
		[mrtTable, rowVirtualizerRef.current]
	);

	return (
		<div ref={rootRef} style={libraryResultsRootStyle}>
			<div style={LibraryResultsGridStyle}>
				<MaterialReactTable table={mrtTable} />
			</div>
			<div style={LibraryResultsGridPaginationStyle}>
				<GridPagination
					onPaginationChanged={props.onPaginationChange}
					pagination={props.pagination}
					count={props.total}
					selectedCount={Object.keys(props.selectedItems).length}
					testId={props.testId}
				/>
			</div>
		</div>
	);
};
Grid.displayName = 'Grid';

export default Grid;
